import React, { useMemo } from "react";
import PropTypes from "prop-types";
import i18n from "i18n-react";
import * as FaIcons from "react-icons/fa";
import DashboardFilters from "../../../components/dashboard/DashboardFilters";

const withDefaultLabel = (options) => {
    return options.map((option, index) => {
        if (index === 0 && option.value === "" && option.label === "") {
            return {
                ...option,
                label: i18n.translate("default_all")
            };
        }
        return option;
    });
};

const OrdersDashboardFilters = ({
    filters,
    filterErrors,
    maxDate,
    orgNodes,
    visibilityMode,
    loadingFilters,
    onApplyPreset,
    onSetCustomDate,
    onUpdateType,
    onUpdateFilter,
    onSearch
}) => {
    const typeOptions = useMemo(() => ([
        { value: "", label: i18n.translate("default_all") },
        { value: "3", label: i18n.translate("default_user") },
        { value: "1", label: i18n.translate("default_profile") },
        { value: "2", label: i18n.translate("default_group") }
    ]), []);
    const statusOptions = useMemo(() => ([
        { value: "", label: i18n.translate("default_all") },
        { value: "0", label: i18n.translate("orders_dashboard_status_pending") },
        { value: "1", label: i18n.translate("orders_dashboard_status_confirmed") },
        { value: "2", label: i18n.translate("orders_dashboard_status_discarded") }
    ]), []);

    const orgConfig = {
        "1": {
            label: i18n.translate("default_profile"),
            options: orgNodes.profiles,
            field: "ion"
        },
        "2": {
            label: i18n.translate("default_group"),
            options: orgNodes.groups,
            field: "gon"
        },
        "3": {
            label: i18n.translate("default_user"),
            options: orgNodes.users,
            field: "user"
        }
    };
    const currentOrgConfig = orgConfig[filters.type];
    const currentOrgOptions = withDefaultLabel(currentOrgConfig?.options || []);
    const showOrgFilters = visibilityMode === "org";
    const daysLabel = String(i18n.translate("orders_dashboard_days"));

    const presets = [7, 15, 30].map((days) => ({
        value: days,
        label: `${days} ${daysLabel}`,
        icon: <FaIcons.FaCalendarAlt />
    }));
    const selects = [
        {
            id: "status",
            label: i18n.translate("orders_dashboard_status"),
            options: statusOptions,
            value: filters.status,
            disabled: loadingFilters,
            onChange: (option) => onUpdateFilter("status", option.value)
        }
    ];

    if (showOrgFilters) {
        selects.push({
            id: "type",
            label: i18n.translate("default_type"),
            options: typeOptions,
            value: filters.type,
            disabled: loadingFilters,
            onChange: (option) => onUpdateType(option.value)
        });
    }

    if (showOrgFilters && currentOrgConfig) {
        selects.push({
            id: currentOrgConfig.field,
            label: currentOrgConfig.label,
            options: currentOrgOptions,
            value: filters[currentOrgConfig.field],
            placeholder: i18n.translate("default_please_select"),
            disabled: loadingFilters,
            onChange: (option) => onUpdateFilter(currentOrgConfig.field, option.value)
        });
    }

    return (
        <DashboardFilters
            presets={presets}
            activePreset={filters.preset}
            customLabel={i18n.translate("orders_dashboard_custom")}
            dateStart={filters.dateStart}
            dateEnd={filters.dateEnd}
            maxDate={maxDate}
            fromLabel={i18n.translate("default_from")}
            toLabel={i18n.translate("default_to")}
            searchLabel={i18n.translate("default_search")}
            errors={filterErrors}
            loading={loadingFilters}
            selects={selects}
            onApplyPreset={onApplyPreset}
            onDateChange={onSetCustomDate}
            onSearch={onSearch}
        />
    );
};

OrdersDashboardFilters.propTypes = {
    filters: PropTypes.object.isRequired,
    filterErrors: PropTypes.object,
    maxDate: PropTypes.string,
    orgNodes: PropTypes.object.isRequired,
    visibilityMode: PropTypes.oneOf(["org", "self"]),
    loadingFilters: PropTypes.bool.isRequired,
    onApplyPreset: PropTypes.func.isRequired,
    onSetCustomDate: PropTypes.func.isRequired,
    onUpdateType: PropTypes.func.isRequired,
    onUpdateFilter: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired
};

OrdersDashboardFilters.defaultProps = {
    filterErrors: {},
    maxDate: "",
    visibilityMode: "org"
};

export default OrdersDashboardFilters;
